/**
 * @file Client error report shaping (F1.9, F6.1, 1.13.6).
 *
 * A copy of ecommerce-platform's `src/lib/observability/errorReport.ts`. Two
 * things differ on purpose: `KNOWN_ROUTE_SEGMENTS` lists this app's routes,
 * not the storefront's, and the consent note below names this app's rule.
 * Everything else, the redaction above all, is kept in step with the
 * storefront (CLAUDE.md section 5), because the API redacts again with the
 * same rules and the two passes are meant to agree.
 *
 * Turns a thrown value into the smallest record that is still useful to an
 * operator: what kind of failure it was, a bounded one-line message, the
 * route pattern it happened on, and the X-Request-Id of the last API call so
 * the browser event lines up with the API and worker logs.
 *
 * Everything here is a redaction boundary, not a convenience. Error reporting
 * is not analytics and carries no personal data, so:
 *
 * - the route is reduced to its pattern (`/downloads/:param`), never the
 *   concrete path, because download tokens, order numbers and intake IDs are
 *   path segments in the storefront, and the magic-link token arrives on
 *   `/auth/verify` here;
 * - the query string and fragment are dropped entirely (magic-link and
 *   download tokens arrive that way);
 * - the message is stripped of anything that looks like an address, a URL, a
 *   long token or a run of digits before it is truncated.
 *
 * Nothing in a report is read from a form, from a cookie or from browser
 * storage, and nothing is stored, so it needs no consent mechanism here
 * (CLAUDE.md rule 8): it is a bug report about our own code, sent to our own
 * API.
 */
import type { ClientErrorKind } from './kinds';

/** Longest message we keep. Stack traces and long validation text are cut. */
export const MAX_MESSAGE_LENGTH = 200;

/** Placeholder written over anything that could carry a value or a token. */
export const REDACTED = '[redacted]';

/** Placeholder written over a path segment that is not a known static route part. */
export const PARAM_SEGMENT = ':param';

/**
 * Static path segments used by the route table in `src/app/router.tsx`.
 * Any other segment is a parameter and is replaced, so a mistyped or crafted
 * path can never carry a token or an address into a report.
 * `errorReport.test.ts` keeps this list honest by reading the route table and
 * failing when it gains a static segment that is missing here.
 */
export const KNOWN_ROUTE_SEGMENTS: readonly string[] = ['account', 'auth', 'membership', 'verify'];

/** One bounded, de-identified record of a client-side failure. */
export interface ClientErrorReport {
  /** Which path raised it: a route render, a global error or a rejected promise. */
  readonly kind: ClientErrorKind;
  /** Error constructor name, for example "TypeError". */
  readonly name: string;
  /** Redacted, single-line, truncated message. */
  readonly message: string;
  /** Route pattern, for example "/account/orders/:param". */
  readonly route: string;
  /** X-Request-Id of the last API call, or null when there was none. */
  readonly requestId: string | null;
}

/** What a caller hands to the reporter. */
export interface ClientErrorInput {
  readonly kind: ClientErrorKind;
  /** The thrown value, whatever it turned out to be. */
  readonly error: unknown;
  /** `location.pathname` at the time of the failure. */
  readonly pathname: string;
  /** Correlation ID; the reporter fills it in when omitted. */
  readonly requestId?: string | null;
}

/**
 * Reads a name and a raw message off an unknown thrown value. Strings, Errors
 * and objects with a `message` all occur in practice; anything else becomes a
 * fixed label rather than a stringified payload we have not inspected.
 */
export function describeError(error: unknown): { name: string; message: string } {
  if (error instanceof Error) {
    return { name: error.name, message: error.message };
  }
  if (typeof error === 'string') {
    return { name: 'Error', message: error };
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const { message } = error;
    if (typeof message === 'string') {
      return { name: error.constructor.name, message };
    }
  }
  return { name: 'Unknown', message: 'non-error value thrown' };
}

/**
 * Removes anything value-shaped from a message, collapses it to one line and
 * truncates it. Run before truncation so a secret cannot survive by sitting
 * past the cut and then being revealed by a later message of another length.
 */
export function redactMessage(raw: string): string {
  const redacted = raw
    // Addresses.
    .replace(/[^\s@]+@[^\s@]+\.[^\s@]+/g, REDACTED)
    // Absolute URLs, which may carry a token in the path or the query.
    .replace(/[a-z][a-z0-9+.-]*:\/\/\S+/gi, REDACTED)
    // Anything after a query separator, wherever it appears.
    .replace(/[?#]\S+/g, REDACTED)
    // Long opaque runs: tokens, IDs, hashes, base64.
    .replace(/[A-Za-z0-9_-]{20,}/g, REDACTED)
    // Runs of digits: order numbers, card fragments, phone numbers.
    .replace(/\d{6,}/g, REDACTED)
    .replace(/\s+/g, ' ')
    .trim();
  return redacted.length > MAX_MESSAGE_LENGTH
    ? `${redacted.slice(0, MAX_MESSAGE_LENGTH - 1)}…`
    : redacted;
}

/**
 * Reduces a concrete pathname to its route pattern. The query string and the
 * fragment are dropped before anything else, then every segment that is not a
 * known static route part becomes `:param`.
 */
export function routePattern(pathname: string): string {
  const path = pathname.split(/[?#]/)[0] ?? '/';
  const segments = path.split('/').filter((segment) => segment.length > 0);
  if (segments.length === 0) return '/';
  const pattern = segments
    .map((segment) => (KNOWN_ROUTE_SEGMENTS.includes(segment) ? segment : PARAM_SEGMENT))
    .join('/');
  return `/${pattern}`;
}

/** Builds the record that is sent or logged. */
export function buildClientErrorReport(
  input: ClientErrorInput,
  requestId: string | null,
): ClientErrorReport {
  const { name, message } = describeError(input.error);
  return {
    kind: input.kind,
    name,
    message: redactMessage(message),
    route: routePattern(input.pathname),
    requestId: input.requestId ?? requestId,
  };
}

/**
 * Identity used for de-duplication. The request ID is deliberately left out:
 * the same bug on the same route is one report however many API calls
 * preceded it, which is what stops a render loop from sending forever.
 */
export function reportKey(report: ClientErrorReport): string {
  return `${report.kind}|${report.name}|${report.route}|${report.message}`;
}
